// Shared IOC detail helpers — extracted from IocPopover.tsx + new utilities.

import type { IocCategoryRef } from '../../lib/graphApi';

export function getVirusTotalUrl(type: string, value: string): string | null {
  const t = type.toUpperCase();
  if (['IP', 'IPV6'].includes(t)) return `https://www.virustotal.com/gui/ip-address/${value}`;
  if (t === 'DOMAIN') return `https://www.virustotal.com/gui/domain/${value}`;
  if (['SHA256', 'SHA1', 'MD5', 'SHA512'].includes(t)) return `https://www.virustotal.com/gui/file/${value}`;
  if (t === 'URL') {
    const encoded = btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `https://www.virustotal.com/gui/url/${encoded}`;
  }
  return null;
}

export function severityColor(severity: string | null | undefined): string {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL': return 'var(--ioc-popover-severity-critical, #dc2626)';
    case 'HIGH': return 'var(--ioc-popover-severity-high, #ea580c)';
    case 'MEDIUM': return 'var(--ioc-popover-severity-medium, #ca8a04)';
    case 'LOW': return 'var(--ioc-popover-severity-low, #16a34a)';
    default: return 'var(--text-muted)';
  }
}

export function getNvdUrl(cveId: string): string {
  return `https://nvd.nist.gov/vuln/detail/${cveId}`;
}

export function getMitreAttackUrl(type: string, value: string): string | null {
  const v = value.toUpperCase();
  switch (type.toUpperCase()) {
    case 'MITRE_TECHNIQUE':
    case 'TECHNIQUE': {
      const parts = v.split('.');
      return parts.length > 1
        ? `https://attack.mitre.org/techniques/${parts[0]}/${parts[1]}/`
        : `https://attack.mitre.org/techniques/${v}/`;
    }
    case 'MITRE_TACTIC': return `https://attack.mitre.org/tactics/${v}/`;
    case 'MITRE_GROUP': return `https://attack.mitre.org/groups/${v}/`;
    case 'MITRE_SOFTWARE': return `https://attack.mitre.org/software/${v}/`;
    case 'MITRE_CAMPAIGN': return `https://attack.mitre.org/campaigns/${v}/`;
    case 'MITRE_MITIGATION': return `https://attack.mitre.org/mitigations/${v}/`;
    default: return null;
  }
}

// ── External links: per-type lookup table ─────────────────────────────────

export interface ExternalLink {
  name: string;          // display label, e.g. "VirusTotal", "blockchain.com"
  url: string;
  group: string;         // bucket: "hashes" | "network" | "mitre" | "crypto" | "handles" | "misc"
}

// Strip a single leading "@" from social handles (Telegram, Twitter, etc.).
function stripAt(v: string): string {
  return v.startsWith('@') ? v.slice(1) : v;
}

// Crypto explorer URL builders — one per supported coin.
const CRYPTO_LINKS: Record<string, (v: string) => ExternalLink> = {
  BITCOIN:       (v) => ({ name: 'blockchain.com', group: 'crypto', url: `https://www.blockchain.com/btc/address/${v}` }),
  BITCOIN_CASH:  (v) => ({ name: 'blockchain.com', group: 'crypto', url: `https://www.blockchain.com/bch/address/${v}` }),
  BITCOINCASH:   (v) => ({ name: 'blockchain.com', group: 'crypto', url: `https://www.blockchain.com/bch/address/${v}` }),
  ETHEREUM:      (v) => ({ name: 'etherscan.io',   group: 'crypto', url: `https://etherscan.io/address/${v}` }),
  MONERO:        (v) => ({ name: 'blockchair',     group: 'crypto', url: `https://blockchair.com/monero/address/${v}` }),
  LITECOIN:      (v) => ({ name: 'blockchair',     group: 'crypto', url: `https://blockchair.com/litecoin/address/${v}` }),
  DOGECOIN:      (v) => ({ name: 'blockchair',     group: 'crypto', url: `https://blockchair.com/dogecoin/address/${v}` }),
  ZCASH:         (v) => ({ name: 'blockchair',     group: 'crypto', url: `https://blockchair.com/zcash/address/${v}` }),
  DASHCOIN:      (v) => ({ name: 'blockchair',     group: 'crypto', url: `https://blockchair.com/dash/address/${v}` }),
  SOLANA:        (v) => ({ name: 'solscan',        group: 'crypto', url: `https://solscan.io/account/${v}` }),
  TRON:          (v) => ({ name: 'tronscan',       group: 'crypto', url: `https://tronscan.org/#/address/${v}` }),
  RIPPLE:        (v) => ({ name: 'xrpscan',        group: 'crypto', url: `https://xrpscan.com/account/${v}` }),
  CARDANO:       (v) => ({ name: 'cardanoscan',    group: 'crypto', url: `https://cardanoscan.io/address/${v}` }),
  STELLAR:       (v) => ({ name: 'stellar.expert', group: 'crypto', url: `https://stellar.expert/explorer/public/account/${v}` }),
  TEZOS:         (v) => ({ name: 'tzkt',           group: 'crypto', url: `https://tzkt.io/${v}` }),
};

// Social handle URL builders — direct platform profile URL.
const HANDLE_LINKS: Record<string, (v: string) => ExternalLink> = {
  TELEGRAM_HANDLE:    (v) => ({ name: 't.me',           group: 'handles', url: `https://t.me/${stripAt(v)}` }),
  TELEGRAMHANDLE:     (v) => ({ name: 't.me',           group: 'handles', url: `https://t.me/${stripAt(v)}` }),
  TWITTER_HANDLE:     (v) => ({ name: 'x.com',          group: 'handles', url: `https://x.com/${stripAt(v)}` }),
  TWITTERHANDLE:      (v) => ({ name: 'x.com',          group: 'handles', url: `https://x.com/${stripAt(v)}` }),
  GITHUB_HANDLE:      (v) => ({ name: 'github.com',     group: 'handles', url: `https://github.com/${stripAt(v)}` }),
  GITHUBHANDLE:       (v) => ({ name: 'github.com',     group: 'handles', url: `https://github.com/${stripAt(v)}` }),
  FACEBOOK_HANDLE:    (v) => ({ name: 'facebook.com',   group: 'handles', url: `https://www.facebook.com/${stripAt(v)}` }),
  FACEBOOKHANDLE:     (v) => ({ name: 'facebook.com',   group: 'handles', url: `https://www.facebook.com/${stripAt(v)}` }),
  INSTAGRAM_HANDLE:   (v) => ({ name: 'instagram.com',  group: 'handles', url: `https://www.instagram.com/${stripAt(v)}` }),
  INSTAGRAMHANDLE:    (v) => ({ name: 'instagram.com',  group: 'handles', url: `https://www.instagram.com/${stripAt(v)}` }),
  LINKEDIN_HANDLE:    (v) => ({ name: 'linkedin.com',   group: 'handles', url: `https://www.linkedin.com/in/${stripAt(v)}` }),
  LINKEDINHANDLE:     (v) => ({ name: 'linkedin.com',   group: 'handles', url: `https://www.linkedin.com/in/${stripAt(v)}` }),
  YOUTUBE_HANDLE:     (v) => ({ name: 'youtube.com',    group: 'handles', url: `https://www.youtube.com/@${stripAt(v)}` }),
  YOUTUBEHANDLE:      (v) => ({ name: 'youtube.com',    group: 'handles', url: `https://www.youtube.com/@${stripAt(v)}` }),
  YOUTUBE_CHANNEL:    (v) => ({ name: 'youtube.com',    group: 'handles', url: `https://www.youtube.com/channel/${v}` }),
  YOUTUBECHANNEL:     (v) => ({ name: 'youtube.com',    group: 'handles', url: `https://www.youtube.com/channel/${v}` }),
  WHATSAPP_HANDLE:    (v) => ({ name: 'wa.me',          group: 'handles', url: `https://wa.me/${stripAt(v).replace(/[^0-9]/g, '')}` }),
  WHATSAPPHANDLE:     (v) => ({ name: 'wa.me',          group: 'handles', url: `https://wa.me/${stripAt(v).replace(/[^0-9]/g, '')}` }),
  PINTEREST_HANDLE:   (v) => ({ name: 'pinterest.com',  group: 'handles', url: `https://www.pinterest.com/${stripAt(v)}` }),
  PINTERESTHANDLE:    (v) => ({ name: 'pinterest.com',  group: 'handles', url: `https://www.pinterest.com/${stripAt(v)}` }),
};

const MITRE_TYPES = new Set([
  'MITRE_TECHNIQUE', 'TECHNIQUE',
  'MITRE_TACTIC', 'MITRE_GROUP', 'MITRE_SOFTWARE',
  'MITRE_CAMPAIGN', 'MITRE_MITIGATION',
]);

const HASH_TYPES = new Set(['SHA256', 'SHA1', 'MD5', 'SHA512']);

/**
 * Return the set of useful third-party links for an IOC of a given type.
 *
 * Dispatches by uppercased `type` value across hashes, network IOCs, MITRE
 * ATT&CK objects, crypto wallets, social handles and misc. Endpoint artifacts
 * (filepath, registry, PDB, YARA) and most misc types intentionally return no
 * links — there is no useful generic destination for them.
 */
export function getExternalLinks(type: string, value: string): ExternalLink[] {
  if (!type || !value) return [];
  const t = type.toUpperCase();
  const links: ExternalLink[] = [];

  // CVE → NVD
  if (t === 'CVE') {
    links.push({ name: 'NVD', group: 'cve', url: getNvdUrl(value) });
    return links;
  }

  // Hashes → VirusTotal (file)
  if (HASH_TYPES.has(t)) {
    const vt = getVirusTotalUrl(t, value);
    if (vt) links.push({ name: 'VirusTotal', group: 'hashes', url: vt });
    return links;
  }

  // Network → VirusTotal + AbuseIPDB / urlhaus / mxtoolbox (where applicable)
  if (t === 'IP' || t === 'IPV4' || t === 'IPV6') {
    const vt = getVirusTotalUrl(t, value);
    if (vt) links.push({ name: 'VirusTotal', group: 'network', url: vt });
    links.push({ name: 'AbuseIPDB', group: 'network', url: `https://www.abuseipdb.com/check/${value}` });
    return links;
  }
  if (t === 'DOMAIN') {
    const vt = getVirusTotalUrl(t, value);
    if (vt) links.push({ name: 'VirusTotal', group: 'network', url: vt });
    links.push({ name: 'MX Toolbox', group: 'network', url: `https://mxtoolbox.com/SuperTool.aspx?action=mx%3a${encodeURIComponent(value)}` });
    return links;
  }
  if (t === 'URL') {
    const vt = getVirusTotalUrl(t, value);
    if (vt) links.push({ name: 'VirusTotal', group: 'network', url: vt });
    links.push({ name: 'urlhaus', group: 'network', url: `https://urlhaus.abuse.ch/browse.php?search=${encodeURIComponent(value)}` });
    return links;
  }

  // MITRE ATT&CK
  if (MITRE_TYPES.has(t)) {
    const mitre = getMitreAttackUrl(t, value);
    if (mitre) links.push({ name: 'ATT&CK', group: 'mitre', url: mitre });
    return links;
  }

  // Crypto
  const crypto = CRYPTO_LINKS[t];
  if (crypto) {
    links.push(crypto(value));
    return links;
  }

  // Social handles
  const handle = HANDLE_LINKS[t];
  if (handle) {
    links.push(handle(value));
    return links;
  }

  // Endpoint, ASN, CIDR, ONION, EMAIL, misc — no useful generic external link.
  return links;
}

/**
 * Build the H1 prefix shown above the raw IOC value.
 *
 * Prefers the registry-resolved category label ("Bitcoin address", "Telegram
 * handle"). Falls back to a title-cased version of the raw `type` field when
 * the category is unknown. For MITRE objects the value is appended (e.g.
 * "MITRE Tactic TA0001").
 */
export function formatCategoryTitle(
  category: IocCategoryRef | null | undefined,
  type: string | null | undefined,
  value: string,
): string {
  const t = (type || '').toUpperCase();

  if (t === 'CVE') return `CVE ${value}`;

  if (category) {
    switch (category.group) {
      case 'crypto':   return `${category.label} address`;
      case 'handles':  return `${category.label} handle`;
      case 'mitre':    return `MITRE ${category.label} ${value}`;
      case 'hashes':   return `${category.label === 'Hashes' ? t || 'Hash' : category.label} hash`;
      case 'network':  return network_title(category.label, category.key);
      case 'endpoint': return endpoint_title(category.label, category.key);
      case 'misc':     return category.label;
      default:         return category.label;
    }
  }

  // Fallback when registry has no entry — show the raw type readably.
  if (!t) return 'Indicator';
  return t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function network_title(label: string, key: string): string {
  switch (key) {
    case 'ip':     return 'IP address';
    case 'domain': return 'Domain';
    case 'url':    return 'URL';
    case 'email':  return 'Email address';
    case 'asn':    return 'ASN';
    case 'cidr':   return 'CIDR range';
    case 'onion':  return 'Onion address';
    default:       return label;
  }
}

function endpoint_title(label: string, key: string): string {
  switch (key) {
    case 'filepath': return 'File path';
    case 'registry': return 'Registry key';
    case 'pdb':      return 'PDB path';
    case 'yara':     return 'YARA rule';
    default:         return label;
  }
}
